import { useState, useEffect, useMemo, useCallback } from 'react'
import { adminApi } from '../services/ApiService'
import { useDeviceStatus } from '../context/DeviceStatusContext'
import {
  Activity, Search, AlertCircle, XCircle, Wifi, Clock, Filter, RefreshCw, Download,
  TrendingUp, Cloud, Database, Code, HardDrive, DollarSign, AlertTriangle, Server, BarChart3,
  ChevronDown, ChevronRight, Zap, CheckCircle, Radio, WifiOff
} from 'lucide-react'
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import Pagination from '../components/Pagination'
import './SystemHub.css'

const ITEMS_PER_PAGE = 50

const LAMBDA_FUNCTIONS = [
  { key: 'telemetry-handler',      logGroup: '/aws/lambda/growpro-telemetry-handler' },
  { key: 'status-handler',         logGroup: '/aws/lambda/growpro-status-handler' },
  { key: 'command-handler',        logGroup: '/aws/lambda/growpro-command-handler' },
  { key: 'response-correlator',    logGroup: '/aws/lambda/growpro-response-correlator' },
  { key: 'websocket-manager',      logGroup: '/aws/lambda/growpro-websocket-manager' },
  { key: 'admin-handler',          logGroup: '/aws/lambda/growpro-admin-handler' },
  { key: 'auth-authorizer',        logGroup: '/aws/lambda/growpro-auth-authorizer' },
  { key: 'photo-handler',          logGroup: '/aws/lambda/growpro-photo-handler' },
  { key: 'subscription-handler',   logGroup: '/aws/lambda/growpro-subscription-handler' },
  { key: 'mp-webhook-handler',     logGroup: '/aws/lambda/growpro-mp-webhook-handler' },
  { key: 'subscription-checker',   logGroup: '/aws/lambda/growpro-subscription-checker' },
  { key: 'data-handler',            logGroup: '/aws/lambda/growpro-data-handler' },
  { key: 'admin-logs-handler',      logGroup: '/aws/lambda/growpro-admin-logs-handler' },
  { key: 'admin-commands-handler',  logGroup: '/aws/lambda/growpro-admin-commands-handler' },
  { key: 'ai-handler',              logGroup: '/aws/lambda/growpro-ai-handler' },
  { key: 'photo-merger',            logGroup: '/aws/lambda/growpro-photo-merger' },
  { key: 'photo-purge',             logGroup: '/aws/lambda/growpro-photo-purge' },
  { key: 'pre-signup',              logGroup: '/aws/lambda/growpro-pre-signup' },
  { key: 'wc-webhook-handler',      logGroup: '/aws/lambda/growpro-wc-webhook-handler' },
  { key: 'dlq-consumer',            logGroup: '/aws/lambda/growpro-dlq-consumer' },
]

export default function SystemHub() {
  const [tab, setTab] = useState('logs')
  const { wsDevices, wsConnected, wsStats, connectionLog } = useDeviceStatus()

  // ── Logs state ──
  const [logs, setLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsError, setLogsError] = useState(null)
  const [logFilter, setLogFilter] = useState('lifecycle')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedMac, setSelectedMac] = useState('all')
  const [logTimeRange, setLogTimeRange] = useState(24)
  const [devices, setDevices] = useState([])
  const [logStats, setLogStats] = useState(null)
  const [logPage, setLogPage] = useState(1)

  // ── Lifecycle / audit state ──
  const [lifecycleEvents, setLifecycleEvents] = useState([])
  const [lifecycleLoading, setLifecycleLoading] = useState(false)
  const [lifecycleError, setLifecycleError] = useState(null)
  const [lifecycleMac, setLifecycleMac] = useState('all')
  const [lifecycleEventType, setLifecycleEventType] = useState('all')
  const [lifecycleHours, setLifecycleHours] = useState(24)

  // ── Log groups state ──
  const [logGroups, setLogGroups] = useState([])
  const [selectedLogGroup, setSelectedLogGroup] = useState('')

  // ── Costs state ──
  const [costs, setCosts] = useState(null)
  const [metrics, setMetrics] = useState(null)
  const [costAlerts, setCostAlerts] = useState([])
  const [costTimeRange, setCostTimeRange] = useState('7')
  const [costsLoading, setCostsLoading] = useState(false)
  const [costsError, setCostsError] = useState(null)

  // ── Lambda Board state ──
  const [lambdaBoard, setLambdaBoard] = useState({})
  const [lambdaBoardHours, setLambdaBoardHours] = useState(24)
  const [lambdaBoardLoading, setLambdaBoardLoading] = useState(false)
  const [expandedFn, setExpandedFn] = useState(null)
  const [selectedLambdaLogs, setSelectedLambdaLogs] = useState(null)
  const [selectedLambdaKey, setSelectedLambdaKey] = useState(null)
  const [lambdaLogsLoading, setLambdaLogsLoading] = useState(false)

  // ── Dashboard metrics state ──
  const [dashData, setDashData] = useState(null)
  const [dashLoading, setDashLoading] = useState(false)

  // ── WebSocket connections from DynamoDB ──
  const [wsConns, setWsConns] = useState(null)
  const [wsConnsLoading, setWsConnsLoading] = useState(false)
  const [dashError, setDashError] = useState(null)
  const [dashHours, setDashHours] = useState('6')

  // ── Logs logic ──
  useEffect(() => { fetchDevices(); fetchLogGroups() }, [])

  useEffect(() => {
    if (logFilter === 'lifecycle') {
      fetchLifecycle()
    } else if (logFilter !== 'all' || selectedMac !== 'all') {
      fetchLogs()
    }
    setLogPage(1)
  }, [logFilter, selectedMac, logTimeRange, selectedLogGroup])

  useEffect(() => {
    if (logFilter === 'lifecycle') fetchLifecycle()
  }, [lifecycleMac, lifecycleHours])

  const fetchLogGroups = async () => {
    try {
      const data = await adminApi.get('/admin/logs/groups')
      if (data.success && data.groups) setLogGroups(data.groups)
    } catch (e) { console.error('Error fetching log groups:', e) }
  }

  const fetchDevices = async () => {
    try {
      const data = await adminApi.get('/admin/devices')
      if (data.success) setDevices(data.devices || [])
    } catch (e) { console.error('Error fetching devices:', e) }
  }

  const fetchLogs = async () => {
    try {
      setLogsLoading(true); setLogsError(null)
      let url = ''
      const mac = selectedMac !== 'all' ? selectedMac : null
      const lgParam = selectedLogGroup ? `&logGroup=${encodeURIComponent(selectedLogGroup)}` : ''

      switch (logFilter) {
        case 'errors': url = `/admin/logs/errors?hours=${logTimeRange}${mac ? `&mac=${mac}` : ''}${lgParam}`; break
        case 'disconnections': url = `/admin/logs/disconnections?hours=${logTimeRange}${mac ? `&mac=${mac}` : ''}${lgParam}`; break
        case 'search':
          if (!searchTerm) return
          url = `/admin/logs/search?q=${encodeURIComponent(searchTerm)}&hours=${logTimeRange}${lgParam}`; break
        default:
          if (mac) url = `/admin/logs/device/${mac}?hours=${logTimeRange}`
          else return
      }

      const data = await adminApi.get(url)
      if (data.success) {
        setLogs(data.logs || [])
        if (mac && logFilter !== 'search') fetchDeviceStats(mac)
      } else setLogsError(data.message || 'Error al cargar logs')
    } catch (e) {
      setLogsError(e.message || 'Error de conexión')
    } finally { setLogsLoading(false) }
  }

  const fetchDeviceStats = async (mac) => {
    try {
      const data = await adminApi.get(`/admin/logs/device/${mac}/stats`, { hours: logTimeRange })
      if (data.success) setLogStats(data)
    } catch (e) { console.error(e) }
  }

  const fetchLifecycle = useCallback(async () => {
    setLifecycleLoading(true); setLifecycleError(null)
    try {
      const mac = lifecycleMac !== 'all' ? lifecycleMac : null
      const params = `hours=${lifecycleHours}&limit=500${mac ? `&mac=${mac}` : ''}`
      const data = await adminApi.get(`/admin/logs/lifecycle?${params}`)
      if (data.success) setLifecycleEvents(data.events || [])
      else setLifecycleError(data.message || 'Error al cargar auditoría')
    } catch (e) {
      setLifecycleError(e.message || 'Error de conexión')
    } finally { setLifecycleLoading(false) }
  }, [lifecycleMac, lifecycleHours])

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchTerm.trim()) { setLogFilter('search'); fetchLogs() }
  }

  const exportLogs = () => {
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `growpro-logs-${new Date().toISOString()}.json`
    a.click(); URL.revokeObjectURL(url)
  }

  const getLogLevel = (msg) => {
    const m = msg.toLowerCase()
    if (m.includes('error') || m.includes('fatal') || m.includes('exception')) return 'error'
    if (m.includes('warn')) return 'warning'
    if (m.includes('disconnect') || m.includes('timeout')) return 'disconnect'
    if (m.includes('connect')) return 'success'
    return 'info'
  }

  const fmtDate = (ts) => new Date(ts).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  })

  // ── Costs logic ──
  const fetchCosts = useCallback(async () => {
    setCostsLoading(true); setCostsError(null)
    try {
      const end = new Date()
      const start = new Date(); start.setDate(start.getDate() - parseInt(costTimeRange))
      const [costsRes, metricsRes, alertsRes] = await Promise.all([
        adminApi.get('/admin/monitoring/costs', {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          granularity: 'DAILY'
        }),
        adminApi.get('/admin/monitoring/metrics'),
        adminApi.get('/admin/monitoring/alerts')
      ])
      // Normalize costs: backend returns { data: { services, totalCost: { amount } } }
      const cd = costsRes?.data || costsRes || {}
      setCosts({
        costsByService: Object.fromEntries(
          Object.entries(cd.services || {}).map(([k, v]) => [k, typeof v === 'object' ? v.cost : v])
        ),
        totalCost: cd.totalCost?.amount ?? cd.totalCost ?? 0,
        budgetStatus: cd.budgetStatus || {},
        recommendations: cd.recommendations || [],
        period: cd.period
      })
      // Normalize metrics: backend returns { metrics: { mqttMessagesIn, lambdaInvocations, ... } }
      const m = metricsRes?.metrics || metricsRes || {}
      setMetrics({
        mqttIn: m.mqttMessagesIn ?? 0,
        mqttOut: m.mqttMessagesOut ?? 0,
        lambdaInvocations: m.lambdaInvocations ?? 0,
        lambdaErrors: m.lambdaErrors ?? 0,
        lambdaDuration: m.lambdaAvgDuration ?? 0,
        apiRequests: m.apiRequests ?? 0
      })
      setCostAlerts(alertsRes.alerts || [])
    } catch (e) { setCostsError(e.message) }
    finally { setCostsLoading(false) }
  }, [costTimeRange])

  useEffect(() => {
    if (tab === 'costs') {
      fetchCosts()
      const iv = setInterval(fetchCosts, 60000)
      return () => clearInterval(iv)
    }
  }, [tab, fetchCosts])

  const fmtCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n)

  const svcIcon = (s) => {
    const map = { 'IoT Core': Cloud, 'Lambda': Code, 'DynamoDB': Database, 'S3': HardDrive, 'CloudWatch': Activity }
    const Icon = map[s] || DollarSign
    return <Icon size={18} />
  }

  // ── Dashboard metrics logic ──
  const fetchDashboard = useCallback(async () => {
    setDashLoading(true); setDashError(null)
    try {
      const data = await adminApi.get('/admin/monitoring/dashboard', { hours: dashHours })
      if (data.success) setDashData(data.sections)
      else setDashError(data.message || 'Error al cargar métricas')
    } catch (e) { setDashError(e.message) }
    finally { setDashLoading(false) }
  }, [dashHours])

  useEffect(() => {
    if (tab === 'metrics') {
      fetchDashboard()
      const iv = setInterval(fetchDashboard, 120000)
      return () => clearInterval(iv)
    }
  }, [tab, fetchDashboard])

  const LAMBDA_COLORS = {
    'telemetry-handler':    '#3b82f6',
    'status-handler':       '#f59e0b',
    'command-handler':      '#10b981',
    'response-correlator':  '#8b5cf6',
    'websocket-manager':    '#ef4444',
    'admin-handler':        '#ec4899',
    'auth-authorizer':      '#6366f1',
    'photo-handler':        '#06b6d4',
    'subscription-handler': '#a855f7',
    'mp-webhook-handler':   '#14b8a6',
    'subscription-checker': '#f97316',
    'data-handler':           '#64748b',
    'admin-logs-handler':     '#0ea5e9',
    'admin-commands-handler': '#d946ef',
    'ai-handler':             '#22c55e',
    'photo-merger':           '#f43f5e',
    'photo-purge':            '#78716c',
    'pre-signup':             '#84cc16',
    'wc-webhook-handler':     '#fb923c',
    'dlq-consumer':           '#94a3b8',
  }

  const LAMBDA_LABELS = {
    'telemetry-handler':    'Telemetry',
    'status-handler':       'Status',
    'command-handler':      'Command',
    'response-correlator':  'Correlator',
    'websocket-manager':    'WebSocket',
    'admin-handler':        'Admin',
    'auth-authorizer':      'Auth',
    'photo-handler':        'Photo',
    'subscription-handler': 'Subs Handler',
    'mp-webhook-handler':   'MercadoPago',
    'subscription-checker': 'Subs Checker',
    'data-handler':           'Data',
    'admin-logs-handler':     'Admin Logs',
    'admin-commands-handler': 'Admin Cmds',
    'ai-handler':             'AI',
    'photo-merger':           'Photo Merger',
    'photo-purge':            'Photo Purge',
    'pre-signup':             'Pre-Signup',
    'wc-webhook-handler':     'WooCommerce',
    'dlq-consumer':           'DLQ Consumer',
  }

  const buildChartData = (lambdaData, metric) => {
    if (!lambdaData) return []
    const timeMap = {}
    Object.entries(lambdaData).forEach(([fn, metrics]) => {
      (metrics[metric] || []).forEach(({ t, v }) => {
        if (!timeMap[t]) timeMap[t] = { t }
        timeMap[t][fn] = Math.round(v * 100) / 100
      })
    })
    return Object.values(timeMap).sort((a, b) => a.t - b.t)
  }

  const buildSimpleChartData = (dataObj, metricKey) => {
    if (!dataObj) return []
    const timeMap = {}
    Object.entries(dataObj).forEach(([label, metrics]) => {
      (metrics[metricKey] || []).forEach(({ t, v }) => {
        if (!timeMap[t]) timeMap[t] = { t }
        timeMap[t][label] = Math.round(v * 100) / 100
      })
    })
    return Object.values(timeMap).sort((a, b) => a.t - b.t)
  }

  const fmtChartTime = (t) => new Date(t).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })

  // ── Lambda Board logic ──
  const fetchLambdaBoard = useCallback(async () => {
    setLambdaBoardLoading(true)
    const results = await Promise.allSettled(
      LAMBDA_FUNCTIONS.map(fn =>
        adminApi.get(`/admin/logs/errors?hours=${lambdaBoardHours}&logGroup=${encodeURIComponent(fn.logGroup)}`)
          .then(data => ({ key: fn.key, errors: data.success ? (data.events || data.logs || []) : [], ok: data.success }))
          .catch(e => ({ key: fn.key, errors: [], ok: false, err: e.message }))
      )
    )
    const board = {}
    results.forEach(r => { if (r.status === 'fulfilled') board[r.value.key] = r.value })
    setLambdaBoard(board)
    setLambdaBoardLoading(false)
  }, [lambdaBoardHours])

  const fetchLambdaRecentLogs = useCallback(async (fnKey) => {
    const fn = LAMBDA_FUNCTIONS.find(f => f.key === fnKey)
    if (!fn) return
    setLambdaLogsLoading(true)
    setSelectedLambdaKey(fnKey)
    setSelectedLambdaLogs(null)
    try {
      const data = await adminApi.get(`/admin/logs/recent?logGroup=${encodeURIComponent(fn.logGroup)}&hours=${lambdaBoardHours}&limit=100`)
      setSelectedLambdaLogs(data.success ? (data.logs || data.events || []) : [])
    } catch (e) {
      console.error('Error fetching recent logs:', e)
      setSelectedLambdaLogs([])
    } finally {
      setLambdaLogsLoading(false)
    }
  }, [lambdaBoardHours])

  useEffect(() => {
    if (tab === 'lambdas') {
      fetchLambdaBoard()
      const iv = setInterval(fetchLambdaBoard, 120000)
      return () => clearInterval(iv)
    }
  }, [tab, fetchLambdaBoard])

  // Re-fetch selected Lambda logs when time range changes
  useEffect(() => {
    if (selectedLambdaKey) fetchLambdaRecentLogs(selectedLambdaKey)
  }, [lambdaBoardHours])

  // Load WS connections from DynamoDB when websocket tab selected
  const loadWSConns = useCallback(async () => {
    setWsConnsLoading(true)
    try {
      const data = await adminApi.get('/admin/websocket/connections')
      setWsConns(data)
    } catch (e) { console.error('WS connections error:', e) }
    finally { setWsConnsLoading(false) }
  }, [])

  useEffect(() => {
    if (tab === 'websocket') {
      loadWSConns()
      const iv = setInterval(loadWSConns, 30000)
      return () => clearInterval(iv)
    }
  }, [tab, loadWSConns])

  // ── Render ──
  return (
    <div className="sh-page">
      <div className="sh-header">
        <div>
          <h1 className="sh-title">Sistema</h1>
          <p className="sh-subtitle">Logs, métricas y costos de la infraestructura</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="sh-tabs">
        <button className={`sh-tab ${tab === 'logs' ? 'active' : ''}`} onClick={() => setTab('logs')}>
          <Activity size={16} /> Logs
        </button>
        <button className={`sh-tab ${tab === 'costs' ? 'active' : ''}`} onClick={() => setTab('costs')}>
          <DollarSign size={16} /> Costos AWS
        </button>
        <button className={`sh-tab ${tab === 'metrics' ? 'active' : ''}`} onClick={() => setTab('metrics')}>
          <BarChart3 size={16} /> Métricas
        </button>
        <button className={`sh-tab ${tab === 'lambdas' ? 'active' : ''}`} onClick={() => setTab('lambdas')}>
          <Zap size={16} /> Lambdas
        </button>
        <button className={`sh-tab ${tab === 'websocket' ? 'active' : ''}`} onClick={() => setTab('websocket')}>
          <Radio size={16} /> WebSocket
        </button>
      </div>

      {/* ════════════ LOGS TAB ════════════ */}
      {tab === 'logs' && (
        <div className="sh-section">
          {/* Mode pills */}
          <div className="sh-log-mode-row">
            {[
              ['lifecycle', 'Auditoría'],
              ['errors', 'Errores'],
              ['disconnections', 'Desconexiones'],
              ['all', 'Por dispositivo'],
            ].map(([k, l]) => (
              <button key={k} className={`sh-mode-pill ${logFilter === k ? 'active' : ''}`} onClick={() => setLogFilter(k)}>
                {k === 'lifecycle' && <Clock size={14} />}
                {k === 'errors' && <AlertCircle size={14} />}
                {k === 'disconnections' && <XCircle size={14} />}
                {k === 'all' && <Search size={14} />}
                {l}
              </button>
            ))}
          </div>

          {/* ── LIFECYCLE / AUDIT MODE ── */}
          {logFilter === 'lifecycle' && (() => {
            const EVENT_LABELS = {
              connected:               'conexión',
              disconnected:            'desconexión',
              command_restart:         'reinicio',
              command_switch:          'switch admin',
              command_ota:             'OTA admin',
              command_config:          'config admin',
              bulk_restart:            'reinicio masivo',
              bulk_ota:                'OTA masivo',
              device_registered:       'registro dispositivo',
              subscription_authorized: 'suscripción activa',
              subscription_cancelled:  'suscripción cancelada',
              subscription_paused:     'suscripción pausada',
              subscription_pending:    'suscripción pendiente',
              subscription_payment:    'pago recibido',
              user_config:             'config usuario',
              user_switch:             'switch usuario',
            }
            const isCmd = e => !!e && (e.startsWith('command_') || e.startsWith('bulk_'))
            const isSub = e => !!e && e.startsWith('subscription_')
            const isUser = e => !!e && e.startsWith('user_')
            const isDevice = e => !!e && !isCmd(e) && !isSub(e) && !isUser(e) && e !== 'device_registered'

            const dotColor = e => {
              if (e === 'connected') return '#10b981'
              if (e === 'disconnected') return '#f87171'
              if (e === 'device_registered') return '#818cf8'
              if (isCmd(e)) return '#f59e0b'
              if (isSub(e)) return '#14b8a6'
              if (isUser(e)) return '#60a5fa'
              return '#6b7280'
            }

            const badgeStyle = e => {
              if (e === 'connected') return { background: 'rgba(16,185,129,0.15)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)' }
              if (e === 'disconnected') return { background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' }
              if (e === 'device_registered') return { background: 'rgba(129,140,248,0.12)', color: '#818cf8', border: '1px solid rgba(129,140,248,0.25)' }
              if (isCmd(e)) return { background: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.25)' }
              if (isSub(e)) return { background: 'rgba(20,184,166,0.12)', color: '#2dd4bf', border: '1px solid rgba(20,184,166,0.25)' }
              if (isUser(e)) return { background: 'rgba(96,165,250,0.12)', color: '#93c5fd', border: '1px solid rgba(96,165,250,0.25)' }
              return { background: 'rgba(107,114,128,0.12)', color: '#9ca3af', border: '1px solid rgba(107,114,128,0.25)' }
            }

            const fmtDetails = (e, raw) => {
              if (!raw) return null
              try {
                const d = typeof raw === 'string' ? JSON.parse(raw) : raw
                if (isSub(e)) {
                  const parts = []
                  if (d.plan) parts.push(`Plan: ${d.plan}`)
                  if (d.billing) parts.push(d.billing === 'annual' ? 'anual' : 'mensual')
                  if (d.amount) parts.push(`$${d.amount}`)
                  if (d.payerEmail) parts.push(d.payerEmail)
                  return parts.join(' · ')
                }
                if (e === 'user_config' || e === 'command_config') {
                  const skip = ['userId', 'timestamp', 'requestId']
                  const parts = Object.entries(d).filter(([k]) => !skip.includes(k)).map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
                  return parts.slice(0, 4).join(' · ')
                }
                if (e === 'command_switch' || e === 'user_switch') {
                  const parts = []
                  if (d.switchIndex !== undefined) parts.push(`switch ${d.switchIndex}`)
                  if (d.state) parts.push(`→ ${d.state}`)
                  if (d.type) parts.push(`(${d.type})`)
                  return parts.join(' ')
                }
                if (e === 'device_registered') return `thingName: ${d.thingName || '—'}, by: ${d.registeredBy || '—'}`
                const str = JSON.stringify(d)
                return str.length > 80 ? str.slice(0, 80) + '…' : str
              } catch { return String(raw).slice(0, 80) }
            }

            const activeTypes = {
              all: 'Todos',
              device: 'Dispositivos',
              commands: 'Comandos admin',
              subscription: 'Suscripciones',
              user: 'Usuario',
            }

            const filtered = lifecycleEvents.filter(ev => {
              if (lifecycleEventType === 'all') return true
              if (lifecycleEventType === 'device') return isDevice(ev.event)
              if (lifecycleEventType === 'commands') return isCmd(ev.event)
              if (lifecycleEventType === 'subscription') return isSub(ev.event) || ev.event === 'subscription_payment'
              if (lifecycleEventType === 'user') return isUser(ev.event)
              return true
            })

            // Stats
            const stats = {
              connections: lifecycleEvents.filter(e => e.event === 'connected').length,
              disconnections: lifecycleEvents.filter(e => e.event === 'disconnected').length,
              commands: lifecycleEvents.filter(e => isCmd(e.event)).length,
              subscriptions: lifecycleEvents.filter(e => isSub(e.event)).length,
              devices: new Set(lifecycleEvents.filter(e => isDevice(e.event) || isCmd(e.event)).map(e => e.mac)).size
            }

            const fmtRelTime = (ts) => {
              const diff = Date.now() - ts
              if (diff < 60000) return 'ahora'
              if (diff < 3600000) return `${Math.floor(diff / 60000)}m`
              if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`
              return `${Math.floor(diff / 86400000)}d`
            }

            const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE)
            const paginated = filtered.slice((logPage - 1) * ITEMS_PER_PAGE, logPage * ITEMS_PER_PAGE)

            return (
              <>
                {/* Controls */}
                <div className="sh-lc-controls">
                  <select value={lifecycleMac} onChange={e => setLifecycleMac(e.target.value)} className="sh-select">
                    <option value="all">Todos los dispositivos</option>
                    {devices.map(d => <option key={d.$id} value={d.MAC}>{d.name || d.MAC}</option>)}
                  </select>
                  <select value={lifecycleHours} onChange={e => setLifecycleHours(parseInt(e.target.value))} className="sh-select">
                    <option value={6}>6 horas</option>
                    <option value={24}>24 horas</option>
                    <option value={72}>3 días</option>
                    <option value={168}>1 semana</option>
                  </select>
                  <button onClick={fetchLifecycle} disabled={lifecycleLoading} className="sh-action-btn primary">
                    <RefreshCw size={14} className={lifecycleLoading ? 'sh-spinning' : ''} /> Actualizar
                  </button>
                </div>

                {/* Stats row */}
                {lifecycleEvents.length > 0 && (
                  <div className="sh-lc-stats">
                    {[
                      { label: 'Total', val: lifecycleEvents.length, color: '#6b7280' },
                      { label: 'Conexiones', val: stats.connections, color: '#10b981' },
                      { label: 'Desconexiones', val: stats.disconnections, color: '#f87171' },
                      { label: 'Suscripciones', val: stats.subscriptions, color: '#14b8a6' },
                      { label: 'Comandos', val: stats.commands, color: '#f59e0b' },
                      { label: 'Dispositivos únicos', val: stats.devices, color: '#818cf8' },
                    ].map(s => (
                      <div key={s.label} className="sh-lc-stat">
                        <span className="sh-lc-stat-val" style={{ color: s.color }}>{s.val}</span>
                        <span className="sh-lc-stat-label">{s.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Type filter pills */}
                <div className="sh-lc-type-pills">
                  {Object.entries(activeTypes).map(([k, l]) => (
                    <button key={k} className={`sh-type-pill ${lifecycleEventType === k ? 'active' : ''}`} onClick={() => { setLifecycleEventType(k); setLogPage(1) }}>
                      {l}
                    </button>
                  ))}
                </div>

                {lifecycleError && <div className="sh-error"><AlertCircle size={18} /> {lifecycleError}</div>}
                {lifecycleLoading && <div className="sh-loading"><div className="sh-spinner" /> Cargando auditoría...</div>}

                {!lifecycleLoading && !lifecycleError && filtered.length === 0 && (
                  <div className="sh-empty"><Clock size={40} /><p>Sin eventos en el período seleccionado</p></div>
                )}

                {!lifecycleLoading && !lifecycleError && filtered.length > 0 && (
                  <>
                    <div className="sh-lc-list">
                      {paginated.map((ev, i) => {
                        const details = fmtDetails(ev.event, ev.details)
                        const isUserEvent = ev.mac?.startsWith('user:')
                        const displayName = isUserEvent
                          ? ev.mac.replace('user:', 'usuario:')
                          : (ev.name || ev.mac)
                        const evColor = dotColor(ev.event)
                        return (
                          <div key={`${ev.mac}-${ev.timestamp}-${i}`} className="sh-lc-item" style={{ borderLeftColor: evColor }}>
                            <span className="sh-lc-dot" style={{ background: evColor }} />
                            <div className="sh-lc-body">
                              <span className="sh-lc-device">{displayName}</span>
                              {!isUserEvent && ev.mac && ev.name && (
                                <code className="sh-lc-mac">{ev.mac}</code>
                              )}
                              <span className="sh-lc-event-label">
                                {EVENT_LABELS[ev.event] || ev.event}
                              </span>
                              {details && <span className="sh-lc-details">{details}</span>}
                            </div>
                            <span className="sh-lc-time" title={new Date(ev.timestamp).toLocaleString('es-ES')}>
                              {new Date(ev.timestamp).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                    <Pagination currentPage={logPage} totalPages={totalPages} totalItems={filtered.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setLogPage} />
                  </>
                )}
              </>
            )
          })()}

          {/* ── CW ERRORS MODE ── */}
          {logFilter === 'errors' && (
            <>
              <div className="sh-lc-controls">
                <select value={selectedMac} onChange={e => setSelectedMac(e.target.value)} className="sh-select">
                  <option value="all">Todos los grupos</option>
                  {devices.map(d => <option key={d.$id} value={d.MAC}>{d.name || d.MAC}</option>)}
                </select>
                <select value={logTimeRange} onChange={e => setLogTimeRange(parseInt(e.target.value))} className="sh-select">
                  <option value={1}>1 hora</option>
                  <option value={6}>6 horas</option>
                  <option value={24}>24 horas</option>
                  <option value={168}>1 semana</option>
                </select>
                {logGroups.length > 0 && (
                  <select value={selectedLogGroup} onChange={e => setSelectedLogGroup(e.target.value)} className="sh-select">
                    <option value="">IoT Core (default)</option>
                    {logGroups.map(g => <option key={g.name} value={g.name}>{g.shortName}</option>)}
                  </select>
                )}
                <button onClick={fetchLogs} disabled={logsLoading} className="sh-action-btn primary">
                  <RefreshCw size={14} className={logsLoading ? 'sh-spinning' : ''} /> Buscar
                </button>
              </div>
              {logsError && <div className="sh-error"><AlertCircle size={18} /> {logsError}</div>}
              {logsLoading && <div className="sh-loading"><div className="sh-spinner" /> Buscando errores...</div>}
              {!logsLoading && !logsError && logs.length === 0 && <div className="sh-empty"><AlertCircle size={40} /><p>Sin errores en el período</p></div>}
              {!logsLoading && !logsError && logs.length > 0 && (() => {
                const totalPages = Math.ceil(logs.length / ITEMS_PER_PAGE)
                const page = logs.slice((logPage - 1) * ITEMS_PER_PAGE, logPage * ITEMS_PER_PAGE)
                return (
                  <div className="sh-logs-container">
                    <div className="sh-logs-count">{logs.length} errores</div>
                    <div className="sh-logs-list">
                      {page.map((log, i) => (
                        <div key={log.eventId || i} className="sh-log-entry sh-log-error">
                          <span className="sh-log-ts">{fmtDate(log.timestamp)}</span>
                          <span className="sh-log-stream">{log.logStreamName}</span>
                          <span className="sh-log-msg">{log.message}</span>
                        </div>
                      ))}
                    </div>
                    <Pagination currentPage={logPage} totalPages={totalPages} totalItems={logs.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setLogPage} />
                  </div>
                )
              })()}
            </>
          )}

          {/* ── CW DISCONNECTIONS MODE ── */}
          {logFilter === 'disconnections' && (
            <>
              <div className="sh-lc-controls">
                <select value={selectedMac} onChange={e => setSelectedMac(e.target.value)} className="sh-select">
                  <option value="all">Todos</option>
                  {devices.map(d => <option key={d.$id} value={d.MAC}>{d.name || d.MAC}</option>)}
                </select>
                <select value={logTimeRange} onChange={e => setLogTimeRange(parseInt(e.target.value))} className="sh-select">
                  <option value={1}>1 hora</option>
                  <option value={6}>6 horas</option>
                  <option value={24}>24 horas</option>
                  <option value={168}>1 semana</option>
                </select>
                <button onClick={fetchLogs} disabled={logsLoading} className="sh-action-btn primary">
                  <RefreshCw size={14} className={logsLoading ? 'sh-spinning' : ''} /> Buscar
                </button>
              </div>
              {logsError && <div className="sh-error"><AlertCircle size={18} /> {logsError}</div>}
              {logsLoading && <div className="sh-loading"><div className="sh-spinner" /> Buscando...</div>}
              {!logsLoading && !logsError && logs.length === 0 && <div className="sh-empty"><XCircle size={40} /><p>Sin desconexiones en el período</p></div>}
              {!logsLoading && !logsError && logs.length > 0 && (() => {
                const totalPages = Math.ceil(logs.length / ITEMS_PER_PAGE)
                const page = logs.slice((logPage - 1) * ITEMS_PER_PAGE, logPage * ITEMS_PER_PAGE)
                return (
                  <div className="sh-logs-container">
                    <div className="sh-logs-count">{logs.length} desconexiones</div>
                    <div className="sh-logs-list">
                      {page.map((log, i) => (
                        <div key={log.eventId || i} className="sh-log-entry sh-log-disconnect">
                          <span className="sh-log-ts">{fmtDate(log.timestamp)}</span>
                          <span className="sh-log-stream">{log.logStreamName}</span>
                          <span className="sh-log-msg">{log.message}</span>
                        </div>
                      ))}
                    </div>
                    <Pagination currentPage={logPage} totalPages={totalPages} totalItems={logs.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setLogPage} />
                  </div>
                )
              })()}
            </>
          )}

          {/* ── POR DISPOSITIVO MODE ── */}
          {logFilter === 'all' && (
            <>
              <div className="sh-lc-controls">
                <select value={selectedMac} onChange={e => setSelectedMac(e.target.value)} className="sh-select">
                  <option value="all">— Seleccionar dispositivo —</option>
                  {devices.map(d => <option key={d.$id} value={d.MAC}>{d.name || d.MAC}</option>)}
                </select>
                <select value={logTimeRange} onChange={e => setLogTimeRange(parseInt(e.target.value))} className="sh-select">
                  <option value={1}>1 hora</option>
                  <option value={6}>6 horas</option>
                  <option value={24}>24 horas</option>
                  <option value={168}>1 semana</option>
                </select>
                <button onClick={fetchLogs} disabled={logsLoading || selectedMac === 'all'} className="sh-action-btn primary">
                  <RefreshCw size={14} className={logsLoading ? 'sh-spinning' : ''} /> Cargar
                </button>
                {logs.length > 0 && (
                  <button onClick={exportLogs} className="sh-action-btn accent"><Download size={14} /> Exportar</button>
                )}
              </div>
              {logStats && (
                <div className="sh-log-stats">
                  {[
                    { icon: Wifi, color: '#10b981', label: 'Conexiones', val: logStats.stats.connections },
                    { icon: XCircle, color: '#f59e0b', label: 'Desconexiones', val: logStats.stats.disconnections },
                    { icon: AlertCircle, color: '#ef4444', label: 'Errores', val: logStats.stats.errors },
                    { icon: Clock, color: '#3b82f6', label: 'Uptime prom.', val: logStats.stats.avgUptime }
                  ].map((s, i) => (
                    <div key={i} className="sh-log-stat">
                      <s.icon size={18} style={{ color: s.color }} />
                      <div><span className="sh-log-stat-label">{s.label}</span><span className="sh-log-stat-value">{s.val}</span></div>
                    </div>
                  ))}
                </div>
              )}
              {logsError && <div className="sh-error"><AlertCircle size={18} /> {logsError}</div>}
              {logsLoading && <div className="sh-loading"><div className="sh-spinner" /> Cargando logs...</div>}
              {selectedMac === 'all' && !logsLoading && (
                <div className="sh-empty"><Search size={40} /><p>Selecciona un dispositivo para ver sus logs</p></div>
              )}
              {!logsLoading && !logsError && selectedMac !== 'all' && logs.length === 0 && (
                <div className="sh-empty"><Activity size={40} /><p>No se encontraron logs</p></div>
              )}
              {!logsLoading && !logsError && logs.length > 0 && (() => {
                const totalPages = Math.ceil(logs.length / ITEMS_PER_PAGE)
                const page = logs.slice((logPage - 1) * ITEMS_PER_PAGE, logPage * ITEMS_PER_PAGE)
                return (
                  <div className="sh-logs-container">
                    <div className="sh-logs-count">{logs.length} eventos</div>
                    <div className="sh-logs-list">
                      {page.map((log, i) => (
                        <div key={log.eventId || i} className={`sh-log-entry sh-log-${getLogLevel(log.message)}`}>
                          <span className="sh-log-ts">{fmtDate(log.timestamp)}</span>
                          <span className="sh-log-stream">{log.logStreamName}</span>
                          <span className="sh-log-msg">{log.message}</span>
                        </div>
                      ))}
                    </div>
                    <Pagination currentPage={logPage} totalPages={totalPages} totalItems={logs.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setLogPage} />
                  </div>
                )
              })()}
            </>
          )}
        </div>
      )}

      {/* ════════════ COSTS TAB ════════════ */}
      {tab === 'costs' && (
        <div className="sh-section">
          {/* Header row */}
          <div className="sh-cost-header">
            <div className="sh-time-btns">
              {['7', '30', '90'].map(d => (
                <button key={d} className={`sh-tbtn ${costTimeRange === d ? 'active' : ''}`} onClick={() => setCostTimeRange(d)}>
                  {d} días
                </button>
              ))}
            </div>
            <button onClick={fetchCosts} disabled={costsLoading} className="sh-action-btn primary">
              <RefreshCw size={14} className={costsLoading ? 'sh-spinning' : ''} />
            </button>
          </div>

          {/* Cost error */}
          {costsError && (
            <div className="sh-error">
              <AlertCircle size={18} /> {costsError}
              <button onClick={fetchCosts} className="sh-action-btn primary" style={{ marginLeft: '1rem' }}>Reintentar</button>
            </div>
          )}

          {/* Cost loading */}
          {costsLoading && !costs && <div className="sh-loading"><div className="sh-load-bar"><div className="sh-load-progress" /></div></div>}

          {/* Cost alerts */}
          {costAlerts.length > 0 && (
            <div className="sh-cost-alerts">
              {costAlerts.map((a, i) => (
                <div key={i} className={`sh-calert sh-calert-${a.severity}`}>
                  {a.severity === 'critical' ? <AlertCircle size={18} /> : <AlertTriangle size={18} />}
                  <div><strong>{a.message}</strong>{a.details && <p>{a.details}</p>}</div>
                </div>
              ))}
            </div>
          )}

          {/* Metrics */}
          {metrics && (
            <div className="sh-metrics-grid">
              {[
                { label: 'MQTT In', value: Math.round(metrics.mqttIn), sub: 'Mensajes recibidos (5min)' },
                { label: 'MQTT Out', value: Math.round(metrics.mqttOut), sub: 'Mensajes enviados (5min)' },
                { label: 'Lambda Invocaciones', value: Math.round(metrics.lambdaInvocations), sub: `Errores: ${Math.round(metrics.lambdaErrors)}` },
                { label: 'Lambda Duración', value: `${metrics.lambdaDuration.toFixed(0)} ms`, sub: 'Promedio' },
                { label: 'API Requests', value: Math.round(metrics.apiRequests), sub: 'Última hora' }
              ].map((m, i) => (
                <div key={i} className="sh-metric">
                  <span className="sh-metric-label">{m.label}</span>
                  <span className="sh-metric-value">{m.value}</span>
                  {m.sub && <span className="sh-metric-sub">{m.sub}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Costs by service */}
          {costs?.costsByService && (
            <>
              <div className="sh-cost-card">
                <div className="sh-cost-card-head">
                  <h3>Costos por Servicio</h3>
                  <span className="sh-total-badge"><DollarSign size={14} /> Total: {fmtCurrency(costs.totalCost)}</span>
                </div>

                <div className="sh-table-wrap">
                  <table className="sh-table">
                    <thead>
                      <tr>
                        <th>Servicio</th>
                        <th style={{ textAlign: 'right' }}>Costo</th>
                        <th style={{ textAlign: 'right' }}>%</th>
                        <th style={{ textAlign: 'right' }}>Presupuesto</th>
                        <th style={{ textAlign: 'right' }}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(costs.costsByService).sort((a, b) => b[1] - a[1]).map(([svc, cost]) => {
                        const pct = (cost / costs.totalCost) * 100
                        const budget = costs.budgetStatus?.[svc] || 0
                        const over = budget > 100, near = budget > 75 && budget <= 100
                        return (
                          <tr key={svc}>
                            <td><span className="sh-svc-name">{svcIcon(svc)} {svc}</span></td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(cost)}</td>
                            <td style={{ textAlign: 'right' }}>{pct.toFixed(1)}%</td>
                            <td style={{ textAlign: 'right' }}>
                              <div className="sh-budget-bar"><div className={`sh-budget-fill ${over ? 'over' : near ? 'near' : ''}`} style={{ width: `${Math.min(budget, 100)}%` }} /></div>
                              <span className="sh-budget-text">{budget.toFixed(1)}%</span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <span className={`sh-status-chip ${over ? 'error' : near ? 'warning' : 'ok'}`}>
                                {over ? 'Excedido' : near ? 'Alerta' : 'Normal'}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recommendations */}
              {costs.recommendations?.length > 0 && (
                <div className="sh-cost-card" style={{ marginTop: '1rem' }}>
                  <h3>Recomendaciones de Optimización</h3>
                  <div className="sh-recs">
                    {costs.recommendations.map((r, i) => (
                      <div key={i} className={`sh-calert sh-calert-${r.priority === 'high' ? 'warning' : 'info'}`}>
                        {r.priority === 'high' ? <AlertTriangle size={18} /> : <AlertCircle size={18} />}
                        <div>
                          <strong>{r.title}</strong>
                          <p>{r.description}</p>
                          {r.potentialSavings && <p className="sh-savings">Ahorro estimado: {fmtCurrency(r.potentialSavings)}/mes</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ════════════ METRICS TAB ════════════ */}
      {tab === 'metrics' && (
        <div className="sh-section">
          <div className="sh-cost-header">
            <div className="sh-time-btns">
              {[['1', '1h'], ['6', '6h'], ['24', '24h'], ['72', '3d']].map(([v, l]) => (
                <button key={v} className={`sh-tbtn ${dashHours === v ? 'active' : ''}`} onClick={() => setDashHours(v)}>{l}</button>
              ))}
            </div>
            <button onClick={fetchDashboard} disabled={dashLoading} className="sh-action-btn primary">
              <RefreshCw size={14} className={dashLoading ? 'sh-spinning' : ''} />
            </button>
          </div>

          {dashError && <div className="sh-error"><AlertCircle size={18} /> {dashError}</div>}

          {dashLoading && !dashData && <div className="sh-loading"><div className="sh-spinner" /> Cargando métricas...</div>}

          {dashData && (
            <div className="sh-dash-grid">
              {/* Lambda Invocations */}
              <div className="sh-chart-card">
                <h3><Code size={16} /> Lambda — Invocaciones</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={buildChartData(dashData.lambda, 'invocations')}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="t" tickFormatter={fmtChartTime} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip labelFormatter={t => new Date(t).toLocaleString('es-ES')} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {Object.entries(LAMBDA_COLORS).map(([fn, color]) => (
                      <Area key={fn} type="monotone" dataKey={fn} name={LAMBDA_LABELS[fn]} stroke={color} fill={color} fillOpacity={0.1} strokeWidth={1.5} dot={false} />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Lambda Errors */}
              <div className="sh-chart-card">
                <h3><AlertCircle size={16} /> Lambda — Errores</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={buildChartData(dashData.lambda, 'errors')}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="t" tickFormatter={fmtChartTime} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip labelFormatter={t => new Date(t).toLocaleString('es-ES')} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {Object.entries(LAMBDA_COLORS).map(([fn, color]) => (
                      <Line key={fn} type="monotone" dataKey={fn} name={LAMBDA_LABELS[fn]} stroke={color} strokeWidth={1.5} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Lambda Duration */}
              <div className="sh-chart-card">
                <h3><Clock size={16} /> Lambda — Duración (ms)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={buildChartData(dashData.lambda, 'duration')}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="t" tickFormatter={fmtChartTime} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip labelFormatter={t => new Date(t).toLocaleString('es-ES')} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v.toFixed(1)} ms`]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {Object.entries(LAMBDA_COLORS).map(([fn, color]) => (
                      <Line key={fn} type="monotone" dataKey={fn} name={LAMBDA_LABELS[fn]} stroke={color} strokeWidth={1.5} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* DynamoDB Capacity */}
              <div className="sh-chart-card">
                <h3><Database size={16} /> DynamoDB — Write Capacity Consumed</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={buildSimpleChartData(dashData.dynamodb, 'consumedWCU')}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="t" tickFormatter={fmtChartTime} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip labelFormatter={t => new Date(t).toLocaleString('es-ES')} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="Connections" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} strokeWidth={1.5} dot={false} />
                    <Area type="monotone" dataKey="Telemetry" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} strokeWidth={1.5} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* DynamoDB Throttles */}
              <div className="sh-chart-card">
                <h3><AlertTriangle size={16} /> DynamoDB — Throttles</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={buildSimpleChartData(dashData.dynamodb, 'writeThrottles')}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="t" tickFormatter={fmtChartTime} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip labelFormatter={t => new Date(t).toLocaleString('es-ES')} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="Connections" stroke="#ef4444" strokeWidth={1.5} dot={false} />
                    <Line type="monotone" dataKey="Telemetry" stroke="#f59e0b" strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* API Gateway */}
              <div className="sh-chart-card">
                <h3><Server size={16} /> API Gateway — Requests & Errores</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={(() => {
                    if (!dashData.apiGateway?.rest) return []
                    const timeMap = {}
                    const addSeries = (data, key) => (data || []).forEach(({ t, v }) => {
                      if (!timeMap[t]) timeMap[t] = { t }
                      timeMap[t][key] = Math.round(v * 100) / 100
                    })
                    addSeries(dashData.apiGateway.rest.count, 'Requests')
                    addSeries(dashData.apiGateway.rest['4xx'], '4xx')
                    addSeries(dashData.apiGateway.rest['5xx'], '5xx')
                    return Object.values(timeMap).sort((a, b) => a.t - b.t)
                  })()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="t" tickFormatter={fmtChartTime} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip labelFormatter={t => new Date(t).toLocaleString('es-ES')} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="Requests" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                    <Line type="monotone" dataKey="4xx" stroke="#f59e0b" strokeWidth={1.5} dot={false} />
                    <Line type="monotone" dataKey="5xx" stroke="#ef4444" strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* API Gateway Latency */}
              <div className="sh-chart-card">
                <h3><TrendingUp size={16} /> API Gateway — Latencia (ms)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={(() => {
                    if (!dashData.apiGateway?.rest?.latency) return []
                    return dashData.apiGateway.rest.latency.map(({ t, v }) => ({ t, Latencia: Math.round(v * 10) / 10 }))
                  })()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="t" tickFormatter={fmtChartTime} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip labelFormatter={t => new Date(t).toLocaleString('es-ES')} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v} ms`]} />
                    <Area type="monotone" dataKey="Latencia" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.15} strokeWidth={1.5} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* IoT Core */}
              <div className="sh-chart-card">
                <h3><Cloud size={16} /> IoT Core — Mensajes MQTT</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={(() => {
                    if (!dashData.iot) return []
                    const timeMap = {}
                    const addSeries = (data, key) => (data || []).forEach(({ t, v }) => {
                      if (!timeMap[t]) timeMap[t] = { t }
                      timeMap[t][key] = Math.round(v)
                    })
                    addSeries(dashData.iot.rulesExecuted, 'Rules Ejecutadas')
                    addSeries(dashData.iot.failures, 'Fallos')
                    addSeries(dashData.iot.ruleThrottled, 'Throttled')
                    return Object.values(timeMap).sort((a, b) => a.t - b.t)
                  })()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="t" tickFormatter={fmtChartTime} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip labelFormatter={t => new Date(t).toLocaleString('es-ES')} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="Rules Ejecutadas" stroke="#10b981" fill="#10b981" fillOpacity={0.15} strokeWidth={1.5} dot={false} />
                    <Area type="monotone" dataKey="Fallos" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} strokeWidth={1.5} dot={false} />
                    <Area type="monotone" dataKey="Throttled" stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} strokeWidth={1.5} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════════ LAMBDAS TAB ════════════ */}
      {tab === 'lambdas' && (
        <div className="sh-section">
          <div className="sh-lambda-toolbar">
            <div className="sh-time-btns">
              {[['1', '1h'], ['24', '24h'], ['168', '7d']].map(([v, l]) => (
                <button
                  key={v}
                  className={`sh-tbtn ${lambdaBoardHours === parseInt(v) ? 'active' : ''}`}
                  onClick={() => setLambdaBoardHours(parseInt(v))}
                >{l}</button>
              ))}
            </div>
            <button onClick={fetchLambdaBoard} disabled={lambdaBoardLoading} className="sh-action-btn primary">
              <RefreshCw size={14} className={lambdaBoardLoading ? 'sh-spinning' : ''} /> Actualizar
            </button>
          </div>

          {lambdaBoardLoading && !Object.keys(lambdaBoard).length && (
            <div className="sh-loading"><div className="sh-spinner" /> Consultando funciones Lambda...</div>
          )}

          <div className="sh-lambda-grid">
            {LAMBDA_FUNCTIONS.map(fn => {
              const color = LAMBDA_COLORS[fn.key] || '#64748b'
              const label = LAMBDA_LABELS[fn.key] || fn.key
              const data = lambdaBoard[fn.key]
              const loading = lambdaBoardLoading && !data
              const errCount = data?.errors?.length ?? 0
              const status = loading ? 'loading' : !data ? 'loading' : errCount === 0 ? 'ok' : errCount <= 3 ? 'warning' : 'error'
              const lastErr = data?.errors?.[0]
              const isExpanded = expandedFn === fn.key

              return (
                <div key={fn.key} className={`sh-lambda-card sh-lambda-card-${status} ${selectedLambdaKey === fn.key ? 'sh-lambda-card-selected' : ''}`} style={{ borderTopColor: color }}>
                  <div
                    className="sh-lambda-card-head"
                    onClick={() => {
                      if (errCount > 0) setExpandedFn(isExpanded ? null : fn.key)
                      if (selectedLambdaKey === fn.key) {
                        setSelectedLambdaKey(null)
                        setSelectedLambdaLogs(null)
                      } else {
                        fetchLambdaRecentLogs(fn.key)
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="sh-lambda-info">
                      <span className={`sh-lambda-dot sh-dot-${status}`} />
                      <div>
                        <div className="sh-lambda-name">{label}</div>
                        <div className="sh-lambda-key">growpro-{fn.key}</div>
                      </div>
                    </div>
                    <div className="sh-lambda-right">
                      {status === 'loading' && <div className="sh-spinner-sm" />}
                      {status === 'ok' && (
                        <span className="sh-lambda-badge sh-lbadge-ok"><CheckCircle size={12} /> Sin errores</span>
                      )}
                      {(status === 'warning' || status === 'error') && (
                        <>
                          <span className={`sh-lambda-badge sh-lbadge-${status}`}>
                            <AlertCircle size={12} /> {errCount} error{errCount !== 1 ? 's' : ''}
                          </span>
                          {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                        </>
                      )}
                    </div>
                  </div>

                  {lastErr && !isExpanded && (
                    <div className="sh-lambda-last-err">
                      <Clock size={11} />
                      <span className="sh-lambda-err-ts">{fmtDate(lastErr.timestamp)}</span>
                      <span className="sh-lambda-err-preview">{lastErr.message?.slice(0, 140)}</span>
                    </div>
                  )}

                  {isExpanded && (
                    <div className="sh-lambda-errors-list">
                      {data.errors.slice(0, 15).map((e, i) => (
                        <div key={e.eventId || i} className="sh-lambda-err-entry">
                          <div className="sh-lambda-err-meta">
                            <span className="sh-log-ts">{fmtDate(e.timestamp)}</span>
                            <span className="sh-log-stream">{e.logStreamName}</span>
                          </div>
                          <pre className="sh-lambda-err-msg">{e.message}</pre>
                        </div>
                      ))}
                      {data.errors.length > 15 && (
                        <div className="sh-lambda-more">+{data.errors.length - 15} errores más — usar filtro Logs para ver todos</div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* ── Recent Logs Panel ── */}
          {selectedLambdaKey && (
            <div className="sh-lambda-logs-panel">
              <div className="sh-lambda-logs-header">
                <h3>
                  <Activity size={16} /> Logs recientes — {LAMBDA_LABELS[selectedLambdaKey] || selectedLambdaKey}
                </h3>
                <button className="sh-lambda-logs-close" onClick={() => { setSelectedLambdaKey(null); setSelectedLambdaLogs(null) }}>
                  <XCircle size={18} />
                </button>
              </div>
              {lambdaLogsLoading && (
                <div className="sh-loading"><div className="sh-spinner" /> Cargando logs recientes...</div>
              )}
              {!lambdaLogsLoading && selectedLambdaLogs && selectedLambdaLogs.length === 0 && (
                <div className="sh-empty"><Activity size={32} /><p>Sin actividad en las últimas {lambdaBoardHours}h</p></div>
              )}
              {!lambdaLogsLoading && selectedLambdaLogs && selectedLambdaLogs.length > 0 && (
                <div className="sh-lambda-logs-list">
                  {selectedLambdaLogs.map((log, i) => (
                    <div key={log.eventId || i} className={`sh-log-entry sh-log-${getLogLevel(log.message)}`}>
                      <span className="sh-log-ts">{fmtDate(log.timestamp)}</span>
                      <span className="sh-log-stream">{log.logStreamName}</span>
                      <span className="sh-log-msg">{log.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* ════════════ WEBSOCKET TAB ════════════ */}
      {tab === 'websocket' && (
        <div className="sh-section">
          {/* Connection status banner */}
          <div className={`sh-ws-banner ${wsConnected ? 'connected' : 'disconnected'}`}>
            {wsConnected
              ? <Wifi size={18} />
              : <WifiOff size={18} />}
            <span className="sh-ws-banner-label">
              {wsConnected ? 'WebSocket conectado' : 'WebSocket desconectado'}
            </span>
            {wsStats && (
              <span className="sh-ws-banner-info">
                {wsStats.uniqueClients ?? '—'} clientes · {wsStats.totalDevices ?? wsDevices?.length ?? '—'} dispositivos
              </span>
            )}
          </div>

          {/* Backend connections stats */}
          {wsConns && (
            <div className="sh-ws-stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
              {[
                { label: 'Conexiones WS', value: wsConns.total ?? 0 },
                { label: 'Admin', value: wsConns.adminConnections ?? 0 },
                { label: 'Usuarios', value: wsConns.userConnections ?? 0 },
                { label: 'Clientes WS local', value: wsStats?.uniqueClients ?? '—' },
                { label: 'Devices en cache', value: wsDevices?.length ?? 0 },
              ].map(s => (
                <div key={s.label} className="sh-ws-stat-card">
                  <div className="sh-ws-stat-value">{s.value}</div>
                  <div className="sh-ws-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* DynamoDB connections table */}
          {wsConnsLoading && !wsConns && (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#64748b' }}>Cargando conexiones...</div>
          )}
          {wsConns?.connections?.length > 0 && (
            <div className="sh-ws-section">
              <h3 className="sh-ws-section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Conexiones activas en DynamoDB ({wsConns.connections.length})</span>
                <button onClick={loadWSConns} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>
                  <RefreshCw size={14} className={wsConnsLoading ? 'sh-spinning' : ''} />
                </button>
              </h3>
              <div className="sh-table-wrap" style={{ maxHeight: '350px', overflow: 'auto' }}>
                <table className="sh-table">
                  <thead>
                    <tr>
                      <th>Connection ID</th>
                      <th>Usuario</th>
                      <th>Rol</th>
                      <th>Conectado</th>
                      <th>Último Ping</th>
                      <th>Suscripciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wsConns.connections.map(conn => (
                      <tr key={conn.connectionId}>
                        <td><code style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{conn.connectionId.slice(0, 12)}…</code></td>
                        <td style={{ fontSize: '0.8rem' }}>{conn.userId?.slice(0, 20) || '—'}</td>
                        <td>
                          <span style={{
                            padding: '0.125rem 0.375rem', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600,
                            background: conn.isAdmin ? 'rgba(167,139,250,0.2)' : 'rgba(59,130,246,0.2)',
                            color: conn.isAdmin ? '#a78bfa' : '#60a5fa',
                          }}>{conn.isAdmin ? 'Admin' : 'User'}</span>
                        </td>
                        <td style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{conn.connectedAt ? new Date(conn.connectedAt).toLocaleTimeString('es-AR') : '—'}</td>
                        <td style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{conn.lastPing ? new Date(conn.lastPing).toLocaleTimeString('es-AR') : '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                            {(conn.subscriptions || []).map(mac => (
                              <span key={mac} style={{ fontSize: '0.6rem', padding: '0.1rem 0.3rem', borderRadius: '3px', background: mac === '__ADMIN__' ? 'rgba(167,139,250,0.15)' : 'rgba(16,185,129,0.15)', color: mac === '__ADMIN__' ? '#c4b5fd' : '#6ee7b7', fontFamily: 'monospace' }}>{mac === '__ADMIN__' ? '🔑 ADMIN' : mac}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Live devices list */}
          <div className="sh-ws-section">
            <h3 className="sh-ws-section-title">
              Dispositivos en memoria ({wsDevices?.length ?? 0})
            </h3>
            {wsDevices && wsDevices.length > 0 ? (
              <div className="sh-ws-devices-grid">
                {wsDevices.map(d => (
                  <div key={d.mac} className="sh-ws-device-chip">
                    <span className={`sh-ws-device-dot ${d.online ? 'online' : 'offline'}`} />
                    <span className="sh-ws-device-name">{d.name || d.mac}</span>
                    <code className="sh-ws-device-mac">{d.mac}</code>
                  </div>
                ))}
              </div>
            ) : (
              <p className="sh-ws-empty">Sin dispositivos en caché WS.</p>
            )}
          </div>

          {/* Connection log */}
          <div className="sh-ws-section">
            <h3 className="sh-ws-section-title">
              Log de conexiones (últimos {connectionLog?.length ?? 0} eventos)
            </h3>
            <div className="sh-ws-conn-log">
              {connectionLog && connectionLog.length > 0 ? connectionLog.map((entry, i) => (
                <div key={i} className={`sh-ws-conn-entry ${entry.event === 'connected' ? 'connected' : 'disconnected'}`}>
                  <span className="sh-ws-conn-icon">
                    {entry.event === 'connected' ? <Wifi size={14} /> : <WifiOff size={14} />}
                  </span>
                  <span className="sh-ws-conn-name">{entry.name || entry.mac}</span>
                  <code className="sh-ws-conn-mac">{entry.mac}</code>
                  <span className="sh-ws-conn-event">
                    {entry.event === 'connected' ? 'Se conectó' : 'Se desconectó'}
                  </span>
                  <span className="sh-ws-conn-time">{new Date(entry.timestamp).toLocaleTimeString('es-AR')}</span>
                </div>
              )) : (
                <div className="sh-ws-conn-empty">Sin eventos de conexión WS registrados.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
